// AccountSessionContext — the only UI source of "am I logged in".
// Reads GET /v1/me. A local flag or leftover localStorage token is not truth.

import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ApiError } from '../api/client'
import { type MeSnapshot, fetchMe, logoutSession, onSessionChanged } from '../api/userland'

export type AccountSessionStatus = 'loading' | 'guest' | 'ready'

export type AccountSessionValue = {
  status: AccountSessionStatus
  me: MeSnapshot | null
  reload: () => Promise<void>
  logout: () => Promise<void>
}

const Ctx = createContext<AccountSessionValue | null>(null)
const ME_SNAPSHOT_KEY = 'wils.me.snapshot'
// A failed read that is not a 401 is asked again: 1 s, doubling, at most 30 s.
const RETRY_FIRST_MS = 1_000
const RETRY_MAX_MS = 30_000

function readRememberedMe(): MeSnapshot | null {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(ME_SNAPSHOT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<MeSnapshot>
    if (parsed.authenticated !== true || !parsed.account_id || !parsed.session_id) return null
    return parsed as MeSnapshot
  } catch {
    return null
  }
}

function writeRememberedMe(snapshot: MeSnapshot | null): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    if (!snapshot) sessionStorage.removeItem(ME_SNAPSHOT_KEY)
    else sessionStorage.setItem(ME_SNAPSHOT_KEY, JSON.stringify(snapshot))
  } catch {
    // storage unavailable
  }
}

export function AccountSessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AccountSessionStatus>(() =>
    readRememberedMe() ? 'ready' : 'loading'
  )
  const [me, setMe] = useState<MeSnapshot | null>(() => readRememberedMe())
  const retry = useRef<{ timer: ReturnType<typeof setTimeout> | null; attempt: number }>({
    timer: null,
    attempt: 0,
  })

  const reload = useMemo(() => {
    const run = async (): Promise<void> => {
      if (retry.current.timer !== null) {
        clearTimeout(retry.current.timer)
        retry.current.timer = null
      }
      try {
        const snapshot = await fetchMe()
        retry.current.attempt = 0
        writeRememberedMe(snapshot)
        setMe(snapshot)
        setStatus('ready')
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          retry.current.attempt = 0
          writeRememberedMe(null)
          setMe(null)
          setStatus('guest')
          return
        }
        // A network error or a 5xx (an API roll) says nothing about the
        // session: keep what is known and ask again, so one failed read does
        // not turn a logged-in user into a guest until a manual reload.
        const delay = Math.min(RETRY_MAX_MS, RETRY_FIRST_MS * 2 ** retry.current.attempt)
        retry.current.attempt += 1
        retry.current.timer = setTimeout(() => void run(), delay)
      }
    }
    return run
  }, [])

  useEffect(() => {
    void reload()
    const stop = onSessionChanged(() => {
      setStatus((current) => (current === 'guest' ? 'loading' : current))
      void reload()
    })
    return () => {
      stop()
      if (retry.current.timer !== null) clearTimeout(retry.current.timer)
      retry.current.timer = null
    }
  }, [reload])

  const value = useMemo<AccountSessionValue>(
    () => ({
      status,
      me,
      reload,
      logout: async () => {
        if (retry.current.timer !== null) clearTimeout(retry.current.timer)
        retry.current.timer = null
        await logoutSession()
        writeRememberedMe(null)
        setMe(null)
        setStatus('guest')
      },
    }),
    [me, reload, status]
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAccountSession(): AccountSessionValue {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAccountSession must be used inside <AccountSessionProvider>')
  return ctx
}

export function useIsGuest(): boolean {
  return useAccountSession().status === 'guest'
}

export function useOptionalAccountSession(): AccountSessionValue | null {
  return useContext(Ctx)
}
